import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import SearchIcon from '../../assets/img/icon-search_white.svg';
import useIsMobile from '../hooks/useIsMobile';
import {
  subPageTitleElementRef,
  subPageTitleScrollProgress,
} from '../utils/scroll/subPageTitleScrollProgress';
import {
  chartHeaderElementRef,
  symbolDetailsScrollProgress,
} from '../utils/scroll/symbolDetailsScrollProgress';

import { HeaderProps } from './Header.types';

import styles from './Header.module.scss';

const MOBILE_HEADER_HEIGHT = 56;
const ANIMATION_RANGE_PX = 40;
const BLUR_STRIP_COUNT = 10;
const BLUR_STRIP_KEYS = Array.from({ length: BLUR_STRIP_COUNT }, (_, index) => `header-strip-${index}`);

const Header: FC<HeaderProps> = ({
  primaryControl,
  centerContent,
  mobileActions,
  desktopActions,
  desktopLeadingActions,
  isSearchVisible,
  onSearchClick,
  isSymbolDetailScreen,
  children,
}: HeaderProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'down' | 'up'>('up');
  const lastScrollTop = useRef(0);
  const lastScrollTopByElement = useRef(new WeakMap<HTMLElement, number>());
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const handleScrollEvent = (event: Event) => {
      const { target } = event;
      let currentScrollTop = 0;

      if (target instanceof HTMLElement) {
        const previousScrollTop = lastScrollTopByElement.current.get(target);
        lastScrollTopByElement.current.set(target, target.scrollTop);
        if (previousScrollTop === undefined || previousScrollTop === target.scrollTop) return;
        currentScrollTop = target.scrollTop;
      } else {
        currentScrollTop = window.scrollY;
      }

      const isCurrentlyScrolled = currentScrollTop > 0;
      const wasScrolled = isScrolled;

      setIsScrolled(isCurrentlyScrolled);

      if (isCurrentlyScrolled && !wasScrolled) {
        setScrollDirection('down');
      } else if (!isCurrentlyScrolled && wasScrolled) {
        setScrollDirection('up');
      }

      lastScrollTop.current = currentScrollTop;

      const activeElementRef = isSymbolDetailScreen ? chartHeaderElementRef : subPageTitleElementRef;
      const activeScrollProgress = isSymbolDetailScreen ? symbolDetailsScrollProgress : subPageTitleScrollProgress;

      if (activeElementRef.current) {
        if (rafId.current !== null) return;
        rafId.current = requestAnimationFrame(() => {
          rafId.current = null;
          if (!activeElementRef.current) return;
          // When the scroll container is at the very top the in-page title is
          // fully visible, so the duplicate header must be fully hidden. Snap to
          // 0 here instead of trusting the subpixel getBoundingClientRect math,
          // which can otherwise leave a tiny residual opacity on some devices.
          if (lastScrollTop.current <= 0) {
            activeScrollProgress.current = 0;
            return;
          }
          const rect = activeElementRef.current.getBoundingClientRect();
          const elementTop = rect.top - MOBILE_HEADER_HEIGHT;
          const progress = Math.min(Math.max(-(elementTop) / ANIMATION_RANGE_PX, 0), 1);
          activeScrollProgress.current = progress;
        });
      }
    };

    // Capture scroll events from any scrollable element
    document.addEventListener('scroll', handleScrollEvent, true);

    return () => {
      document.removeEventListener('scroll', handleScrollEvent, true);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [isScrolled, isSymbolDetailScreen]);

  if (isMobile) {
    return (
      <>
        <div className={classNames([styles.containerMobile, {
          [styles.scrollDirectionDown]: scrollDirection === 'down',
          [styles.scrollDirectionUp]: scrollDirection === 'up',
        }])}
        >
          <div className={styles.blurHeader}>
            <div className={classNames(styles.stripsContainer, { [styles.scrolled]: isScrolled })}>
              <div className={styles.strip} />
              {BLUR_STRIP_KEYS.map(key => (
                <div key={key} className={classNames(styles.strip, styles.mask, styles.overlap)} />
              ))}
            </div>
          </div>
          <div className={styles.contentWrapperMobile}>
            <div className={styles.returnBtnMobile}>{primaryControl}</div>
            {centerContent}
            {mobileActions && <div className={styles.buttonsMobile}>{mobileActions}</div>}
          </div>
        </div>
        {children}
      </>
    );
  }

  return (
    <>
      <div ref={containerRef} className={classNames(styles.container, { [styles.scrolled]: isScrolled })}>
        <div className={styles.contentWrapper}>
          <div className={styles.headerElements}>
            <div className={styles.headerProfileInfo}>
              {desktopLeadingActions}
              {desktopActions}
            </div>
            {isSearchVisible && (
              <div
                className={styles.searchStocksWrapper}
                role="button"
                tabIndex={0}
                onClick={onSearchClick}
                onKeyDown={event => event.key === 'Enter' && onSearchClick?.()}
              >
                <img src={SearchIcon} alt="search stocks" />
                <div>Search (Ctrl+K)</div>
              </div>
            )}
          </div>
          <div className={styles.hiddenHeader} />
        </div>
      </div>
      {children}
    </>
  );
};

export default Header;
